import cookie from 'js-cookie';
import { useSelector } from 'react-redux';

import type { LastInteractedPsychicCookie, RightPsychic } from 'types/objectTypes';
import {
  Block,
  Link,
  Psychic,
} from 'src/__generated__/graphqlTypes';
import { ASK_ACCOUNT_INFO, LAST_INTERACTED_PSYCHIC } from 'constants/constants';
import { Logger } from 'lib/logger';
import { extractNcOffer } from 'src/shared/lib/offers';
import { accountInfoObject } from 'src/shared/lib/cookie/accountInfo';
import { Store } from 'app-redux/types/storeTypes';

import { setMultipleCookies } from './ctaCookie';

import type { PersonalizedButtonRenderer } from '../config/declarations';

export const usePersonalizedButtons = (
  psychic: RightPsychic,
  psychicFrame?: Psychic,
  // @ts-ignore
  render: PersonalizedButtonRenderer,
) => {
  const user = useSelector((store: Store) => store.server.auth.user);

  if (!psychicFrame) {
    return null;
  }

  const personalizedButtons = psychicFrame
    ?.personalizedButtonsCollection
    ?.items as Array<Block | Link>;

  if (!personalizedButtons?.length) {
    return null;
  }

  const buttons = personalizedButtons
    .map((button) => {
      const onClickTierSensitiveButton = (hasDiscount: boolean) => {
        const now = new Date();
        const expires = new Date(now.setDate(now.getDate() + 1));
        const currentAskAccountInfoCookie = cookie.get(ASK_ACCOUNT_INFO);
        let currentAskAccountInfo = accountInfoObject;

        try {
          if (currentAskAccountInfoCookie) {
            currentAskAccountInfo = JSON.parse(currentAskAccountInfoCookie);
          }
        // eslint-disable-next-line no-empty
        } catch (error) { }

        setMultipleCookies({
          [ASK_ACCOUNT_INFO]: {
            ...currentAskAccountInfo,
            extId: psychic.extId,
            chat: false,
          },
        });

        if (user?.isLeadCustomer) {
          const phoneNumber = user.customerPhoneNumber === '9999999999'
            ? ''
            : user.customerPhoneNumber;

          setMultipleCookies({
            [ASK_ACCOUNT_INFO]: {
              ...accountInfoObject,
              extId: psychic.extId,
              custId: user.custId,
              chat: false,
              dob: user.userDateOfBirth,
              ncflowtype: 2,
              email: user.userEmail,
              phoneNumber,
            },
          });
        }

        try {
          const promoCode = extractNcOffer(cookie.get());

          if (!promoCode) {
            return;
          }

          const value: LastInteractedPsychicCookie = {
            id: psychic.extId,
            psychicTier: psychic.groupId,
            basePrice: Number(psychic.basePrice.replace('$', '')),
            promoCode,
            hasDiscount,
          };
          cookie.set(LAST_INTERACTED_PSYCHIC, JSON.stringify(value), { path: '/', expires });
        } catch (error) {
          Logger.error(error);
        }
      };

      if (button.slug === 'discount'
        && psychic.isDiscountedPsychic
        && button.__typename === 'Link'
      ) {
        return render(button as Link, () => onClickTierSensitiveButton(true));
      }

      if (button.slug === 'no-discount'
        && !psychic.isDiscountedPsychic
        && button.__typename === 'Link'
      ) {
        return render(button as Link, () => onClickTierSensitiveButton(false));
      }

      return null;
    })
    .filter(Boolean);

  if (buttons.length) {
    return <>{buttons}</>;
  }

  return null;
};
